import { EVENTOS, type ErrorCode } from '@sol-pool/protocol'
import { en } from './en'
import { idiomaAtual, type Idioma } from './idioma'
import { ptBR } from './pt-BR'
import type { ArgsDe, Chave, Dicionario, Plural, Valores } from './tipos'

/**
 * Internacionalização.
 *
 *   t('lobby.entrar')                       → 'Entrar'
 *   t('emJogo.prazoEm', { min: 5 })         → 'em disputa · prazo em 5 min'
 *   t('retrospecto.vitorias', { count: 1 }) → 'vitória'
 *
 * Tudo aqui é puro: nada de DOM, nada de efeito ao importar. O idioma vem de
 * `idioma.ts`, que também é puro. É o que deixa `efeito.ts` — que roda em
 * teste e dentro da física da mesa — traduzir um rótulo sem arrastar a
 * página junto.
 *
 * O pt-BR é a fonte da verdade: as chaves nascem lá, e o tipo `Chave` vem de
 * lá. Uma chave que não existe não compila.
 */

export type { Chave, Dicionario, Plural } from './tipos'
export {
  IDIOMAS,
  IDIOMA_PADRAO,
  definirIdioma,
  detectarIdioma,
  esquecerIdioma,
  idiomaAtual,
  localeDeFormatacao,
  type Idioma,
} from './idioma'

export const DICIONARIOS: Readonly<Record<Idioma, Dicionario>> = {
  'pt-BR': ptBR,
  en,
}

/** Todas as chaves, na ordem do pt-BR. Para o teste de paridade. */
export const CHAVES = Object.keys(ptBR) as Chave[]

/**
 * Troca cada `{nome}` pelo valor correspondente.
 *
 * NÃO escapa HTML. A maior parte dos textos vai para `textContent` ou para
 * um `innerHTML` cujo parâmetro o chamador já escapou; escapar aqui
 * quebraria quem passa `<strong>…</strong>` de propósito. Um nome sem valor
 * fica como está, entre chaves — é mais fácil de notar na tela do que um
 * buraco.
 */
export function interpolar(texto: string, valores?: Valores): string {
  if (!valores) return texto
  return texto.replace(/\{(\w+)\}/g, (original, nome: string) =>
    nome in valores ? String(valores[nome]) : original,
  )
}

/**
 * Plural mínimo: um é `one`, o resto é `other`.
 *
 * Deliberadamente sem `Intl.PluralRules`: os dois idiomas que temos seguem a
 * mesma regra, e o CLDR do pt-BR trata zero como `one` ("0 vitória"), que
 * não é como ninguém escreve. Quando entrar uma língua com regra diferente,
 * é aqui que ela entra.
 */
function escolherPlural(plural: Plural, valores?: Valores): string {
  return Number(valores?.count) === 1 ? plural.one : plural.other
}

/**
 * Traduz num dicionário específico, com fallback para o pt-BR.
 *
 * O fallback é para quem chega em tempo de execução com um dicionário
 * parcial — o tipo exige todas as chaves, mas um JSON carregado de fora não
 * passa pelo tipo. Uma tradução faltando mostra português, que é melhor que
 * mostrar a chave.
 */
export function traduzirEm(
  dicionario: Partial<Dicionario>,
  chave: Chave,
  valores?: Valores,
): string {
  const valor: string | Plural | undefined = dicionario[chave] ?? ptBR[chave]
  if (valor === undefined) return chave
  const texto = typeof valor === 'string' ? valor : escolherPlural(valor, valores)
  return interpolar(texto, valores)
}

/** O texto de uma chave, no idioma atual. */
export function t<K extends Chave>(chave: K, ...args: ArgsDe<K>): string {
  return traduzirEm(DICIONARIOS[idiomaAtual()], chave, args[0] as Valores | undefined)
}

// ------------------------------------------------------ eventos do servidor

/**
 * Um evento como o servidor o manda: código e parâmetros.
 *
 * Solto de propósito (`string`, não `EventoCode`): o que chega pela rede já
 * passou pelo schema do protocolo, mas este módulo também recebe respostas
 * HTTP lidas com `as`. Código desconhecido devolve `null`, e quem chama cai
 * no texto que veio junto.
 */
export type EventoRecebido = { code: string; params?: Valores | undefined }

/** A chave i18n de um código de evento. Uma convenção, e um teste a fixa. */
export const chaveDoEvento = (code: string) => `evento.${code}` as Chave

export function traduzirEvento(ev: EventoRecebido | null | undefined): string | null {
  if (!ev || !(ev.code in EVENTOS)) return null
  return traduzirEm(DICIONARIOS[idiomaAtual()], chaveDoEvento(ev.code), ev.params)
}

/**
 * A mensagem para o jogador a partir do que o servidor mandou.
 *
 * Três degraus, do mais preciso ao mais bruto: o EVENTO diz o que aconteceu;
 * a CLASSE (`code`) diz ao menos o tipo; o TEXTO é o que um servidor antigo
 * mandava, e continua servindo quando os dois primeiros faltam.
 */
export function mensagemDoServidor(msg: {
  code?: ErrorCode | undefined
  message?: string | undefined
  evento?: EventoRecebido | undefined
}): string {
  const porEvento = traduzirEvento(msg.evento)
  if (porEvento) return porEvento

  if (msg.code) {
    const chave = `erro.codigo.${msg.code}` as Chave
    if (chave in ptBR) return t(chave as never)
  }

  return msg.message ?? t('erro.inesperado')
}

// ------------------------------------------------------ status e decisões

/** O status como as regras o produzem: código e parâmetros numéricos. */
export type StatusRecebido = { code: string; params?: Record<string, number> | undefined }

/**
 * O que está em jogo agora, no idioma do jogador.
 *
 * Vem de `engine-rules` (mesa local) ou do servidor (`statusCode`), e é o
 * mesmo código nos dois casos. `bola` chega como id e vira cor aqui —
 * palavra é do dicionário. `n` alimenta o plural.
 */
export function traduzirStatus(
  s: StatusRecebido | null | undefined,
  fallback = '',
): string {
  if (!s) return fallback
  const chave = `status.${s.code}` as Chave
  if (!(chave in ptBR)) return fallback

  const valores: Valores = { ...(s.params ?? {}) }
  if (typeof valores.n === 'number') valores.count = valores.n
  if (typeof valores.bola === 'number') {
    const cor = `bola.${valores.bola}` as Chave
    if (cor in ptBR) valores.bola = t(cor as never)
  }
  return traduzirEm(DICIONARIOS[idiomaAtual()], chave, valores)
}

/**
 * O rótulo de uma opção de decisão, pelo ÍNDICE canônico.
 *
 * O índice é o que vai gravado no replay e é estável por contrato; o `label`
 * que as regras mandam é o texto em português, e fica só de reserva.
 */
export function rotuloDaOpcao(indice: number, reserva = ''): string {
  const chave = `decisao.opcao.${indice}` as Chave
  return chave in ptBR ? t(chave as never) : reserva
}

/**
 * Por que há uma escolha aberta ("Quebra irregular"), pelo `kind` que as
 * regras mandam — ou null se a situação não tem texto.
 */
export function motivoDaDecisao(kind: string): string | null {
  const chave = `decisao.${kind}` as Chave
  return chave in ptBR ? t(chave as never) : null
}

/**
 * Como a partida terminou. `reason` é enum do protocolo, logo é código.
 */
export function traduzirFim(reason: string): string {
  const chave = `fim.${reason}` as Chave
  return chave in ptBR ? t(chave as never) : reason
}

/**
 * A regra que encerrou a partida, em uma linha — ou null se não há texto.
 *
 * O código vem das regras (`MatchSummary.ending`); a chave é `fim.motivo.<kind>`.
 * Sem tradução devolve null, e a tela não mostra código cru.
 */
export function traduzirMotivoDoFim(kind: string | null | undefined): string | null {
  if (!kind) return null
  const chave = `fim.motivo.${kind}` as Chave
  return chave in ptBR ? t(chave as never) : null
}

/*
 * Todo código do catálogo tem tradução? Conferido pelo COMPILADOR.
 *
 * Se `EVENTOS` ganhar um código sem entrada `evento.<código>` no pt-BR, a
 * linha abaixo deixa de compilar. O teste faz a mesma conta em tempo de
 * execução — e a faz também para o `en`, que o tipo já obriga a ter as
 * mesmas chaves do pt-BR.
 */
type CodigoSemTraducao = Exclude<`evento.${keyof typeof EVENTOS}`, Chave>
const _todoEventoTemTraducao: [CodigoSemTraducao] extends [never] ? true : CodigoSemTraducao =
  true
void _todoEventoTemTraducao
